import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import LoadingState from 'components/shared/LoadingState';
import { getAppSettings, setAppSetting } from '../../services/api';

// The API stores boolean settings as string 'true'/'false'. Normalise to real
// booleans at the fetch boundary (and serialise back on save) so component
// logic uses idiomatic boolean comparisons. Non-boolean values pass through.
const parseSettings = (raw) =>
  Object.fromEntries(
    Object.entries(raw || {}).map(([key, value]) => [
      key,
      value === 'true' ? true : value === 'false' ? false : value,
    ])
  );

export default function PlatformSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await getAppSettings();
      setSettings(parseSettings(response.settings));
    } catch (err) {
      setLoadError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = async (key) => {
    const nextValue = !settings[key];
    const serialised = nextValue ? 'true' : 'false';
    setSaving(key);

    try {
      const response = await setAppSetting(key, serialised);
      setSettings((prev) => ({ ...prev, [key]: nextValue }));

      let msg = `${key} updated to ${serialised}`;
      if (response.stripe_action) {
        msg += ` — ${response.stripe_action}`;
      }
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  const header = (
    <>
      <h2 className="text-token-xl font-semibold">Platform Settings</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Manage platform-wide settings and feature toggles.
      </p>
    </>
  );

  if (loading) {
    return (
      <div>
        {header}
        <LoadingState message="Loading settings..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        {header}
        <Alert variant="destructive" className="flex items-center justify-between gap-4">
          <AlertDescription>{loadError}</AlertDescription>
          <Button variant="outline" size="sm" onClick={fetchSettings}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const freeTrialEnabled = settings.free_trial_enabled === true;
  const isSavingFreeTrial = saving === 'free_trial_enabled';
  const holdingPageEnabled = settings.holding_page_enabled === true;
  const isSavingHoldingPage = saving === 'holding_page_enabled';
  // Missing key (API not yet redeployed with the seed) reads as disabled —
  // truthful, since the old API also doesn't serve poll_seconds.
  const liveDraftPollEnabled = settings.live_draft_auto_poll_enabled === true;
  const isSavingLiveDraftPoll = saving === 'live_draft_auto_poll_enabled';

  return (
    <div>
      {header}

      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-6">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1 basis-64">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-token-md font-medium">Web App Holding Page</span>
              <Badge variant={holdingPageEnabled ? 'destructive' : 'success'}>
                {holdingPageEnabled ? 'App hidden' : 'App live'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Shows the &ldquo;running final checks&rdquo; maintenance overlay to all web app
              users instead of the app, and a &ldquo;waiting on the official game&rdquo; banner
              across the storefront. Administrators are unaffected and keep full access.
              Turn this off to go live.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="inline-flex h-4 w-4 items-center justify-center">
              {isSavingHoldingPage && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
              )}
            </span>
            <Switch
              checked={holdingPageEnabled}
              onCheckedChange={() => handleToggle('holding_page_enabled')}
              disabled={saving !== null}
            />
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-6">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1 basis-64">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-token-md font-medium">Live Draft Auto-Refresh</span>
              <Badge variant={liveDraftPollEnabled ? 'success' : 'outline'}>
                {liveDraftPollEnabled ? 'Auto (5s)' : 'Manual only'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              The Live Draft helper auto-refreshes picks every ~5 seconds. Turn this off as
              a kill switch if draft-night load misbehaves — polling clients stop within a
              few seconds and fall back to the manual Refresh button. Turning it back on
              only affects users after they reload the app.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="inline-flex h-4 w-4 items-center justify-center">
              {isSavingLiveDraftPoll && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
              )}
            </span>
            <Switch
              checked={liveDraftPollEnabled}
              onCheckedChange={() => handleToggle('live_draft_auto_poll_enabled')}
              disabled={saving !== null}
            />
          </div>
        </div>
      </div>

      <div className="rounded-token-large border border-borderGrey bg-white p-6">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1 basis-64">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-token-md font-medium">Free Trial (KICKOFF Code)</span>
              <Badge variant={freeTrialEnabled ? 'success' : 'outline'}>
                {freeTrialEnabled ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Auto-apply the KICKOFF promo code (first month free) for new users at checkout.
              Toggling this also activates/deactivates the promo code on Stripe.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Fixed-size spinner slot so the Switch never moves while saving */}
            <span className="inline-flex h-4 w-4 items-center justify-center">
              {isSavingFreeTrial && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
              )}
            </span>
            <Switch
              checked={freeTrialEnabled}
              onCheckedChange={() => handleToggle('free_trial_enabled')}
              disabled={saving !== null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
